import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { UserPreference } from './index';
import TimeDropdown from './TimeDropdown';

interface Props {
    preferences: UserPreference[];
    onSave: (
        eventTypeId: number | null,
        channel: string | null,
        body: Partial<Pick<UserPreference, 'enabled' | 'quiet_hours_start' | 'quiet_hours_end' | 'timezone' | 'dnd_until'>>,
    ) => void | Promise<void>;
}

/**
 * Quiet hours = a daily window during which push channels are suppressed.
 * In-app notifications still arrive (they don't ding); only OS banners /
 * mobile notifications are suppressed.
 *
 * Stored on the GLOBAL preference row (event_type_id=null, channel=null).
 * Both endpoints (start/end) are HH:mm:ss strings. We render two time inputs
 * + a master enabled toggle (turning off the toggle = clear the times).
 */
const QuietHoursSection: React.FC<Props> = ({ preferences, onSave }) => {
    const intl = useIntl();
    // Global pref row is the one with both event_type_id and channel = null.
    // It's also where dispatcher reads quiet_hours_start/end from.
    const global = preferences.find((p) => p.event_type_id === null && p.channel === null);

    const [enabled, setEnabled] = useState<boolean>(false);
    const [start, setStart] = useState<string>('20:00');
    const [end, setEnd] = useState<string>('08:00');

    useEffect(() => {
        const s = global?.quiet_hours_start;
        const e = global?.quiet_hours_end;
        setEnabled(!!s && !!e);
        if (s) setStart(s.substring(0, 5));
        if (e) setEnd(e.substring(0, 5));
    }, [global?.quiet_hours_start, global?.quiet_hours_end]);

    const persist = useCallback((nextEnabled: boolean, nextStart: string, nextEnd: string) => {
        const startStr = nextEnabled ? `${nextStart}:00` : null;
        const endStr = nextEnabled ? `${nextEnd}:00` : null;
        onSave(null, null, {
            quiet_hours_start: startStr,
            quiet_hours_end: endStr,
        });
    }, [onSave]);

    const handleToggle = () => {
        const next = !enabled;
        setEnabled(next);
        persist(next, start, end);
    };

    // Custom TimeDropdown fires onChange with the final HH:mm — commit
    // immediately (no need for the old onBlur trick the native input required).
    const handleStartChange = useCallback((nextStart: string) => {
        setStart(nextStart);
        if (enabled) persist(true, nextStart, end);
    }, [enabled, end, persist]);

    const handleEndChange = useCallback((nextEnd: string) => {
        setEnd(nextEnd);
        if (enabled) persist(true, start, nextEnd);
    }, [enabled, start, persist]);

    return (
        <section className="notif-settings__section notif-settings__section--quiet">
            <header className="notif-settings__section-head">
                <span className="notif-settings__section-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </svg>
                </span>
                <div className="notif-settings__section-head-text">
                    <h2 className="notif-settings__section-title">
                        <FormattedMessage id="notifications.settings.quietHours.title" defaultMessage="Quiet hours" />
                    </h2>
                    <p className="notif-settings__section-desc">
                        <FormattedMessage
                            id="notifications.settings.quietHours.desc"
                            defaultMessage="Suppress browser and mobile push during these hours. In-app notifications still arrive — they just don't ring."
                        />
                    </p>
                </div>
                <span className={`notif-settings__pill ${enabled ? 'notif-settings__pill--active' : 'notif-settings__pill--off'}`}>
                    {enabled ? (
                        <FormattedMessage
                            id="notifications.settings.quietHours.pillActive"
                            defaultMessage="AKTIVE {from}–{to}"
                            values={{ from: start, to: end }}
                        />
                    ) : (
                        <FormattedMessage id="notifications.settings.quietHours.pillOff" defaultMessage="JOAKTIVE" />
                    )}
                </span>
            </header>

            <div className="notif-settings__quiet-row">
                <label className="notif-settings__toggle">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={handleToggle}
                    />
                    <span className="notif-settings__toggle-slider" />
                    <span className="notif-settings__toggle-label">
                        <FormattedMessage
                            id={enabled ? 'notifications.settings.quietHours.on' : 'notifications.settings.quietHours.off'}
                            defaultMessage={enabled ? 'On' : 'Off'}
                        />
                    </span>
                </label>

                <div className={`notif-settings__quiet-times ${enabled ? '' : 'is-disabled'}`}>
                    <div className="notif-settings__time-field">
                        <span className="notif-settings__time-label">
                            <FormattedMessage id="notifications.settings.quietHours.from" defaultMessage="From" />
                        </span>
                        <TimeDropdown
                            value={start}
                            onChange={handleStartChange}
                            disabled={!enabled}
                            ariaLabel={intl.formatMessage({ id: 'notifications.settings.quietHours.from', defaultMessage: 'From' })}
                        />
                    </div>
                    <div className="notif-settings__time-field">
                        <span className="notif-settings__time-label">
                            <FormattedMessage id="notifications.settings.quietHours.to" defaultMessage="To" />
                        </span>
                        <TimeDropdown
                            value={end}
                            onChange={handleEndChange}
                            disabled={!enabled}
                            ariaLabel={intl.formatMessage({ id: 'notifications.settings.quietHours.to', defaultMessage: 'To' })}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default QuietHoursSection;
